import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useCarPurchasesApi } from "../../http/carPurchases/api";
import {
  CarCategory,
  FuelType,
  Status,
  TypeOfUse,
  getCategories,
  getFuelTypes,
  getStatus,
  getTypesOfUse,
} from "../../http/cars/types";
import { useLeasingCompaniesApi } from "../../http/leasingCompanies/api";
import { LeasingCompany } from "../../http/leasingCompanies/types";
import { useManufacturersApi } from "../../http/manufacturers/api";
import { Manufacturer } from "../../http/manufacturers/types";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { Subsidiary } from "../../http/subsidiaries/types";
import { useUsersApi } from "../../http/users/api";
import { User } from "../../http/users/types";
import { CarLeasingFormBody } from "../carLeasings/CarLeasingForm";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import FormGrid from "../ui/FormGrid";
import Input from "../ui/Input";
import ModalDialog from "../ui/ModalDialog";
import Numeric from "../ui/Numeric";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Switch from "../ui/Switch";
import Textarea from "../ui/Textarea";

const MANUFACTURER_FIELDS = ["id", "name"] as const;
type ManufacturerField = (typeof MANUFACTURER_FIELDS)[number];
type ManufacturerSearch = Pick<Manufacturer, ManufacturerField>;

const LEASING_COMPANY_FIELDS = ["id", "name"] as const;
type LeasingCompanyFiels = (typeof LEASING_COMPANY_FIELDS)[number];
type LeasingCompanySearch = Pick<LeasingCompany, LeasingCompanyFiels>;

const USER_FIELDS = ["id", "fullName"] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = Pick<User, UserField>;

const SUBSIDIARIES_FIELDS = ["id", "name"] as const;
type SubsidiaryField = (typeof SUBSIDIARIES_FIELDS)[number];
type SubsidiarySearch = Pick<Subsidiary, SubsidiaryField>;

export type NewCarFormBody = {
  vehicleIdentificationNumber: string;
  plateNumber?: string;
  driver?: UserSearch;
  typeOfUse?: TypeOfUse;
  notes?: string;
  manufacturer: ManufacturerSearch;
  model: string;
  hp?: number;
  displacement?: string;
  co2Emissions?: string;
  fuelType?: FuelType;
  category: CarCategory;
  isPrivateCar?: boolean;
  status: Status;
  subsidiary: SubsidiarySearch;
  leasing: CarLeasingFormBody | null;
  registrationDate?: string;
  purchaseDate?: string;
  soldDate?: string;
};

type CarFormProps = {
  isDisabled?: boolean;
  onSubmit: (form: NewCarFormBody) => Promise<void>;
};

const NewCarForm: FC<CarFormProps> = ({ onSubmit, isDisabled }: CarFormProps) => {
  const i18n = useI18n();
  const manufacturerApi = useManufacturersApi();
  const subsidiaryApi = useSubsidiariesApi();
  const leasingCompaniesApi = useLeasingCompaniesApi();
  const carPurchasesApi = useCarPurchasesApi();
  const usersApi = useUsersApi();
  const aborter = useRef(new AbortController());
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useContext(NotificationDispatch);

  const [defaultUserOptions, setDefaultUserOptions] = useState<UserSearch[]>([]);

  const categories = getCategories(i18n.t);
  const status = getStatus(i18n.t);
  const fuelTypes = getFuelTypes(i18n.t);
  const typesOfUse = getTypesOfUse(i18n.t);

  const manufacturers = manufacturerApi.useAdminManufacturers(
    { limit: 100, fields: MANUFACTURER_FIELDS },
    aborter.current.signal,
  );
  const subsidiaries = subsidiaryApi.useAdminSubsidiaries(
    { limit: 100, fields: SUBSIDIARIES_FIELDS },
    aborter.current.signal,
  );
  const leasingCompanies = leasingCompaniesApi.useAdminLeasingCompanies(
    { limit: 100, fields: LEASING_COMPANY_FIELDS },
    aborter.current.signal,
  );

  useEffect(() => {
    (subsidiaries.error || manufacturers.error || leasingCompanies.error) &&
      dispatch(showErrorNotification(i18n, subsidiaries.error || manufacturers.error || leasingCompanies.error));
  }, [subsidiaries.error, manufacturers.error, leasingCompanies.error]);

  const param = new URLSearchParams(window.location.search);
  const carPurchaseId = param.get("car_purchase_id");

  const { data, trigger: triggerCarPurchase } = carPurchasesApi.getAdminCarPurchase(
    carPurchaseId!,
    aborter.current.signal,
  );

  useEffect(() => {
    carPurchaseId && triggerCarPurchase();
  }, [carPurchaseId, triggerCarPurchase]);

  const defaultValues = {
    manufacturer: data?.manufacturer,
    model: data?.model,
    subsidiary: data?.subsidiary,
    driver: data?.driver,
    leasing: data
      ? {
          company: data.leasingCompany,
        }
      : null,
  };

  return (
    <Form<NewCarFormBody>
      defaultValues={defaultValues}
      disabled={isDisabled}
      submitText={i18n.t.common.save}
      onSubmit={() => onSubmit}
    >
      {({ register, formState: { errors }, control, reset, setValue, watch, getValues, trigger }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [data?.id]);

        const isPrivateCar = watch("isPrivateCar");

        return (
          <>
            <FormGrid columns={2}>
              <FormField label={i18n.t.common.vehicleIdentificationNumber} error={errors.vehicleIdentificationNumber}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled}
                    {...register("vehicleIdentificationNumber", {
                      required: true,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>
              <FormField
                label={i18n.t.common.leasing}
                isOptional={!data || data?.purchaseType == "purchase"}
                error={
                  carPurchaseId && data?.purchaseType == "leasing"
                    ? !watch("leasing.company")
                      ? {
                          type: "required",
                          message: i18n.t.common.missingLeasingCompany,
                        }
                      : !watch("leasing.contractDate")
                        ? {
                            type: "required",
                            message: i18n.t.common.missingContractDate,
                          }
                        : !watch("leasing.expirationDate")
                          ? {
                              type: "required",
                              message: i18n.t.common.missingExpirationDate,
                            }
                          : undefined
                    : undefined
                }
              >
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    disabled={isDisabled}
                    isInvalid={isInvalid}
                    onClick={() => setIsOpen(true)}
                    value={watch("leasing")?.company?.name ?? ""}
                  ></Input>
                )}
              </FormField>

              <FormField label={i18n.t.plateNumbers.singular} error={errors.plateNumber}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    disabled={isDisabled}
                    isInvalid={isInvalid}
                    {...register("plateNumber", {
                      required: true,
                    })}
                  />
                )}
              </FormField>
              <Controller
                name="subsidiary"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.subsidiaries.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        isDisabled={isDisabled}
                        options={subsidiaries.data?.items}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        styles={reactSelectStyles<SubsidiarySearch>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="driver"
                control={control}
                rules={{ required: !!carPurchaseId || !!watch("typeOfUse") }}
                render={({ field, fieldState }) => (
                  <FormField
                    label={i18n.t.drivers.singular}
                    error={fieldState.error}
                    isOptional={!carPurchaseId && !watch("typeOfUse")}
                  >
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<UserSearch, UserField>(
                        aborter,
                        searchTerm,
                        usersApi.getAdminUsers,
                        { fields: USER_FIELDS },
                      );

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultUserOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          menuPosition="fixed"
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled}
                          defaultOptions={defaultUserOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.fullName}
                          getOptionValue={(x) => x.id}
                          isClearable
                          styles={reactSelectStyles<UserSearch>(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
              <Controller
                name="typeOfUse"
                control={control}
                rules={{ required: !!carPurchaseId || !!watch("driver") }}
                render={({ field, fieldState }) => (
                  <FormField
                    label={i18n.t.common.typeOfUse}
                    error={fieldState.error}
                    isOptional={!carPurchaseId && !watch("driver")}
                  >
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        isDisabled={isDisabled}
                        inputId={labelId}
                        options={typesOfUse}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        isClearable
                        styles={reactSelectStyles<TypeOfUse>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="manufacturer"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.manufacturers.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        isDisabled={isDisabled}
                        options={manufacturers.data?.items}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        defaultInputValue={data?.manufacturer.name}
                        styles={reactSelectStyles<ManufacturerSearch>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />
              <FormField label={i18n.t.common.model} error={errors.model}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled}
                    {...register("model", {
                      required: true,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              <Controller
                name="hp"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.hp} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        {...field}
                        id={labelId}
                        disabled={isDisabled}
                        isInvalid={isInvalid}
                        onBlur={(value) => setValue("hp", value ?? undefined)}
                        decimalScale={0}
                        allowNegative={false}
                      />
                    )}
                  </FormField>
                )}
              />

              <FormField label={i18n.t.common.engineCapacity} error={errors.displacement} isOptional>
                {({ labelId, isInvalid, isOptional }) => (
                  <Input
                    id={labelId}
                    disabled={isDisabled}
                    isInvalid={isInvalid}
                    {...register("displacement", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              <Controller
                name="fuelType"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.fuelType} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={fuelTypes}
                        isDisabled={isDisabled}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<FuelType>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="status"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.state} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        isDisabled={isDisabled}
                        options={status}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<Status>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="category"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.category} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={categories}
                        isDisabled={isDisabled}
                        isClearable
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<CarCategory>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />
              <FormField label={i18n.t.common.privateCar} error={errors.isPrivateCar} isOptional>
                {({ labelId }) => (
                  <Switch
                    id={labelId}
                    checked={isPrivateCar ?? false}
                    onChange={(checked) => setValue("isPrivateCar", checked)}
                    disabled={isDisabled}
                  />
                )}
              </FormField>

              <Controller
                name="purchaseDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value) {
                      if (new Date(value).getFullYear() < 1000) {
                        return i18n.t.common.invalidDate;
                      }
                      const soldDate = watch("soldDate");
                      if (soldDate && value > soldDate) {
                        return i18n.t.carOrders.errors.carOrderDateAfterSoldDate;
                      }
                    }
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.purchaseDate} error={fieldState.error} isOptional>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => setValue("purchaseDate", v!)}
                        isInvalid={isInvalid}
                        value={watch("purchaseDate")}
                        disabled={isDisabled}
                      />
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="registrationDate"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    if (!value) return i18n.t.common.invalidDate;
                    const d = new Date(value);
                    if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.registrationDate} error={fieldState.error}>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => setValue("registrationDate", v!)}
                        isInvalid={isInvalid}
                        value={watch("registrationDate")}
                        disabled={isDisabled}
                      />
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="soldDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value && new Date(value).getFullYear() < 1000) {
                      return i18n.t.common.invalidDate;
                    }
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.soldDate} error={fieldState.error} isOptional>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => setValue("soldDate", v!)}
                        isInvalid={isInvalid}
                        value={watch("soldDate")}
                        disabled={isDisabled}
                      />
                    )}
                  </FormField>
                )}
              />

              <FormField label={i18n.t.common.co2} error={errors.co2Emissions} isOptional>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    disabled={isDisabled}
                    isInvalid={isInvalid}
                    {...register("co2Emissions", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              <FormField label={i18n.t.common.notes} error={errors.notes} isOptional fullWidth>
                {({ labelId, isOptional }) => (
                  <Textarea
                    id={labelId}
                    disabled={isDisabled}
                    {...register("notes", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>
            </FormGrid>

            {!!isOpen && (
              <ModalDialog
                onClose={() => setIsOpen(false)}
                title={i18n.t.common.leasing}
                isSubmitting={!!isDisabled}
                actions={[
                  {
                    title: i18n.t.common.clear,
                    type: "secondary",
                    onClick: () => {
                      setValue("leasing", null);
                      const currentValues = getValues();
                      reset({
                        ...currentValues,
                        leasing: null,
                      });
                    },
                  },
                  {
                    title: i18n.t.common.ok,

                    onClick: async () => {
                      if (watch("leasing.company.id")) {
                        const valid = await trigger(["leasing.contractDate", "leasing.expirationDate"]);
                        valid && setIsOpen(false);
                      } else {
                        setIsOpen(false);
                      }
                    },
                  },
                ]}
              >
                <FormFieldsContainer>
                  <Controller
                    name="leasing.company"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.t.leasingCompanies.singular} error={fieldState.error}>
                        {({ labelId }) => (
                          <Select
                            menuPosition="fixed"
                            {...field}
                            inputId={labelId}
                            options={leasingCompanies.data?.items}
                            defaultValue={data?.leasingCompany}
                            isDisabled={isDisabled}
                            isClearable={!data?.leasingCompany}
                            placeholder={`${i18n.t.common.select}...`}
                            getOptionLabel={(x) => x.name}
                            getOptionValue={(x) => x.id}
                            styles={reactSelectStyles<LeasingCompanySearch>()}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <Controller
                    name="leasing.contractDate"
                    control={control}
                    rules={{
                      required: true,
                      validate: (value) => {
                        if (!value) return i18n.t.common.invalidDate;
                        const d = new Date(value);
                        if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                        return undefined;
                      },
                    }}
                    render={({ fieldState }) => (
                      <FormField label={i18n.t.common.contractDate} error={fieldState.error}>
                        {({ isInvalid }) => (
                          <DatePicker
                            onChange={(v) => setValue("leasing.contractDate", v!)}
                            isInvalid={isInvalid}
                            value={watch("leasing.contractDate")}
                            disabled={isDisabled}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <Controller
                    name="leasing.expirationDate"
                    control={control}
                    rules={{
                      required: true,
                      validate: (value) => {
                        if (!value) return i18n.t.common.invalidDate;
                        const d = new Date(value);
                        if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                        return undefined;
                      },
                    }}
                    render={({ fieldState }) => (
                      <FormField label={i18n.t.common.expirationDate} error={fieldState.error}>
                        {({ isInvalid }) => (
                          <DatePicker
                            onChange={(v) => setValue("leasing.expirationDate", v!)}
                            isInvalid={isInvalid}
                            value={watch("leasing.expirationDate")}
                            disabled={isDisabled}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <FormField label={i18n.t.common.contractDetails} error={errors.leasing?.details} fullWidth isOptional>
                    {({ labelId }) => <Input id={labelId} disabled={isDisabled} {...register("leasing.details")} />}
                  </FormField>
                </FormFieldsContainer>
              </ModalDialog>
            )}
          </>
        );
      }}
    </Form>
  );
};

export default NewCarForm;
